import { useEffect, useReducer, useRef } from 'react';
import moment from 'moment';

import { facturasIniciales, facturasPorSubir } from './datosSmartScan';

/** Cuánto tarda el "Daemon" de la demo en grabar en Eiffel una factura aprobada. */
const DEMORA_EIFFEL_MS = 5000;

const ahora = () => moment().format('YYYY-MM-DDTHH:mm:ss');

const actualizarFactura = (state, id, cambios) => ({
  ...state,
  facturas: state.facturas.map((factura) => (factura.id === id ? { ...factura, ...cambios(factura) } : factura))
});

const reducer = (state, action) => {
  switch (action.type) {
    case 'asignar':
      return actualizarFactura(state, action.id, (factura) => ({
        [action.grupo]: factura[action.grupo].map((linea) =>
          linea.id === action.lineaId ? { ...linea, itemCompra: action.itemCompra, aprendido: false } : linea
        )
      }));
    case 'editar':
      return actualizarFactura(state, action.id, () => ({ [action.campo]: action.valor }));
    case 'aprobar':
      return actualizarFactura(state, action.id, () => ({
        estado: 'aprobada',
        aprobadaEl: ahora(),
        aprobadaPor: 'Usuario demo',
        eiffel: { estado: 'pendiente', eiffelKey: null, fecha: null, error: null }
      }));
    case 'reintentar':
      return actualizarFactura(state, action.id, (factura) => ({ eiffel: { ...factura.eiffel, estado: 'pendiente', error: null } }));
    case 'grabar':
      return {
        ...actualizarFactura(state, action.id, () => ({
          eiffel: { estado: 'grabado', eiffelKey: `FAA 0001-${String(45219 + state.grabadas).padStart(8, '0')}`, fecha: ahora(), error: null }
        })),
        grabadas: state.grabadas + 1
      };
    case 'subir': {
      const [siguiente, ...resto] = state.porSubir;
      if (!siguiente) return state;
      return { ...state, porSubir: resto, facturas: [{ ...siguiente, recibida: ahora() }, ...state.facturas] };
    }
    default:
      return state;
  }
};

const estadoInicial = () => ({ facturas: facturasIniciales, porSubir: facturasPorSubir, grabadas: 0 });

/**
 * Estado en memoria de la demo: imputar líneas, corregir la cabecera, aprobar (queda en cola y a los pocos
 * segundos aparece grabada en Eiffel) y "subir" la próxima factura de ejemplo. Al salir de la página se pierde.
 */
const useSmartScan = () => {
  const [state, dispatch] = useReducer(reducer, undefined, estadoInicial);
  const timers = useRef([]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const grabarMasTarde = (id) => {
    timers.current.push(setTimeout(() => dispatch({ type: 'grabar', id }), DEMORA_EIFFEL_MS));
  };

  return {
    facturas: state.facturas,
    quedanPorSubir: state.porSubir.length > 0,
    asignar: (id, grupo, lineaId, itemCompra) => dispatch({ type: 'asignar', id, grupo, lineaId, itemCompra }),
    editar: (id, campo, valor) => dispatch({ type: 'editar', id, campo, valor }),
    aprobar: (id) => {
      dispatch({ type: 'aprobar', id });
      grabarMasTarde(id);
    },
    reintentar: (id) => {
      dispatch({ type: 'reintentar', id });
      grabarMasTarde(id);
    },
    subir: () => {
      const siguiente = state.porSubir[0];
      dispatch({ type: 'subir' });
      return siguiente || null;
    }
  };
};

export default useSmartScan;
